import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ChevronDown, MoreHorizontal, Vote } from 'lucide-react';
import { UploadIcon, WarningIcon } from '../../../components/icons';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import QuillEditor from '../../../components/QuillEditor';
import { normalizeRichText } from '../../../components/RichContent';
import Chip from '../../../components/Chip';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import AdminListToolbar from '../../components/AdminListToolbar';
import ListPager from '../../components/ListPager';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';
import {
  MEASURE_TYPES,
  INITIATIVE_SOURCES,
  INITIATIVE_STATUSES,
  STATUS_GROUPS,
  JURISDICTION_LEVELS,
  PUBLICATION_STATUSES,
  NEEDS_PUBLISH_FILTER,
  measureTypeLabel,
  initiativeSourceLabel,
  jurisdictionLabel,
  statusLabel,
  statusVariant,
  publicationLabel,
  publicationTone,
} from '../../../constants/ballotInitiatives';
import { HelpTrigger } from '../../../help';
import type { ElectionCycle } from './CyclesView';
import BallotInitiativeOperations, { type OperationsView } from './BallotInitiativeOperations';
import ExplainerBulkImportModal from './ExplainerBulkImportModal';
import {
  fetchDocumentCoverage,
  normalizeBulkPreview,
  type BulkPreview,
  type DocumentCoverageEntry,
  type DocumentState,
} from '../../../components/ballot-initiatives/operationsApi';
import { initiativeDisplayName, initiativeNumberLine } from '../../../components/ballot-initiatives/initiativeDisplay';

export interface InitiativeRow {
  id: string;
  measure_number: string | null;
  ballot_title: string | null;
  popular_name: string | null;
  unofficial_caption: string | null;
  sos_initiative_number: string | null;
  measure_type: string;
  initiative_source: string;
  jurisdiction_level: string;
  jurisdiction_name: string | null;
  election_calendar_name: string | null;
  election_calendar: {
    id: string;
    name: string | null;
    election_year: number;
    election_type: string | null;
  } | null;
  status: string;
  publication_status: string;
  published_at: string | null;
}

interface ElectoralDistrictOption {
  id: string;
  scope: string;
  display_label: string;
}

interface ImportSnapshot {
  id: string;
  source_code: string;
  source_url: string;
  http_status: number | null;
  sha256: string;
  parse_state: 'pending' | 'parsed' | 'failed';
  failure_message: string | null;
}

interface ImportRun {
  id: string;
  operation: 'index_import' | 'document_acquisition' | 'periodic_refresh';
  mode: 'fixture' | 'live';
  initiative_cycle: string;
  initiative_number: string | null;
  status: 'running' | 'completed' | 'partial' | 'failed';
  summary: Record<string, unknown>;
  snapshots: ImportSnapshot[];
  documents: ImportDocument[];
}

interface ImportDocument {
  id: string;
  document_type: string;
  exact_label: string;
  acquisition_state: 'acquired' | 'failed';
  extraction_state: 'not_applicable' | 'pending' | 'extracted' | 'failed' | 'skipped';
  source_url: string;
  final_url: string;
  sha256: string;
  failure_message: string | null;
}

interface BallotInitiativeImportOptions {
  modes: Array<'fixture' | 'live'>;
  cycles: string[];
  current_cycle: string;
  sources: string[];
  document_types: string[];
}

export function resolveImportCycle(
  selectedCycle: string,
  options: Pick<BallotInitiativeImportOptions, 'cycles' | 'current_cycle'>
): string {
  return options.cycles.includes(selectedCycle) ? selectedCycle : options.current_cycle;
}

export const DEFAULT_OPERATION_MODE = 'live' as const;

export const documentCoverageQueryKey = (
  mode: 'fixture' | 'live',
  cycle: string,
  search: string
) => ['ballot-initiative-document-picker', mode, cycle, search] as const;

export function toggleDocumentTypeSelection(
  current: string[],
  type: string,
  checked: boolean
): string[] {
  const next = new Set(current);
  if (checked) next.add(type); else next.delete(type);
  if (checked && type === 'sos_final_text') next.add('sos_result_page');
  if (checked && type === 'legislative_fiscal') next.add('legislative_detail_page');
  if (checked && type === 'legislative_ballot_analysis_draft') next.add('legislative_ballot_analysis_page');
  if (!checked && type === 'sos_result_page') next.delete('sos_final_text');
  if (!checked && type === 'legislative_detail_page') next.delete('legislative_fiscal');
  if (!checked && type === 'legislative_ballot_analysis_page') next.delete('legislative_ballot_analysis_draft');
  return Array.from(next);
}

const HTML_PDF_DEPENDENCIES: Record<string, string> = {
  sos_result_page: 'sos_final_text',
  legislative_detail_page: 'legislative_fiscal',
  legislative_ballot_analysis_page: 'legislative_ballot_analysis_draft',
};

export function documentActionPreview(
  type: string,
  state: DocumentState | undefined,
  selectedTypes: string[]
): string {
  const dependentPdf = HTML_PDF_DEPENDENCIES[type];
  if (dependentPdf) {
    if (state?.successfulArtifact && selectedTypes.includes(dependentPdf)) {
      return 'reuse retained page as the PDF dependency';
    }
    if (state?.selectedUrl) return 'refresh current official page';
    return state?.disabledReason || 'no current retained official page link is available';
  }
  if (state?.successfulArtifact || state?.state === 'reused') {
    return 'reuse retained artifact';
  }
  if (state?.state === 'eligible') return 'acquire from current official link';
  return state?.disabledReason || 'refresh source HTML and evaluate eligibility';
}

const sourceCycleForRow = (row: InitiativeRow, fallback: string): string => {
  const year = row.election_calendar?.election_year;
  return year ? `${year - 1}-${year}` : fallback;
};

const documentTypeLabel = (type: string): string => ({
  sos_result_page: 'SOS result page',
  sos_final_text: 'SOS final text',
  legislative_detail_page: 'Legislative Council detail page',
  legislative_fiscal: 'Legislative Council fiscal analysis',
  legislative_ballot_analysis_page: 'Ballot analysis detail page',
  legislative_ballot_analysis_draft: 'Ballot analysis draft PDF',
}[type] ?? type.replace(/_/g, ' '));

/** Map jurisdiction_level to the electoral_districts scope(s) that populate the picker. */
export function scopesForJurisdiction(level: string): string[] | null {
  switch (level) {
    case 'district':
      return ['co_state_house', 'co_state_senate', 'us_house_co'];
    case 'county':
      return ['co_county'];
    default:
      return null;
  }
}

const parseInitiatives = (raw: unknown): AdminListResult<InitiativeRow> => {
  const data = raw as { data?: InitiativeRow[]; total?: number; limit?: number; offset?: number };
  const total = data.total ?? 0;
  const limit = data.limit ?? 0;
  const offset = data.offset ?? 0;
  return { rows: data.data ?? [], total, hasMore: offset + limit < total };
};

const filterSelectClasses =
  'h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
const inputClasses =
  'w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const COLUMN_COUNT = 9;

const EMPTY_FORM = {
  ballot_title: '',
  measure_number: '',
  popular_name: '',
  description: '',
  measure_type: '',
  initiative_source: '',
  election_calendar_id: '',
  jurisdiction_level: 'statewide',
  jurisdiction_name: '',
  electoral_district_id: '',
  status: 'proposed',
};

const apiError = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== 'object') return fallback;
  const response = 'response' in error && error.response && typeof error.response === 'object'
    ? error.response as { data?: unknown }
    : null;
  const data = response?.data && typeof response.data === 'object'
    ? response.data as { error?: unknown; message?: unknown }
    : null;
  return typeof data?.error === 'string' ? data.error
    : typeof data?.message === 'string' ? data.message : fallback;
};

/**
 * Ballot Initiatives list — measures scoped to a cycle and jurisdiction
 * (ELECTIONS_TOOL_PLAN.md E4). Search is debounced and errors surface inline
 * with a retry, replacing the legacy page's request-per-keystroke fetch and
 * console-only failures.
 */
const InitiativesView: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [measureType, setMeasureType] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [statusGroup, setStatusGroup] = useState('');
  // A publication value, or NEEDS_PUBLISH_FILTER for the review queue (PRD v2 §5.4.2).
  const [publication, setPublication] = useState('');
  const [blueBookStatus, setBlueBookStatus] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [cycleId, setCycleId] = useState('');
  // Default to 'all': hand-entered measures carry no source observations, and a
  // 'current' default silently hid every one of them (incl. the whole on-ballot
  // set) — which is how a prod admin came to rewrite an importer row into a
  // different measure on 2026-09-01. Narrowing to a lifecycle is opt-in now,
  // and shows as a removable chip while applied.
  const [active, setActive] = useState('');
  const [operationsView, setOperationsView] = useState<OperationsView>('initiatives');
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [explainerImportOpen, setExplainerImportOpen] = useState(false);
  const [importAction, setImportAction] = useState<'indexes' | 'periodic'>('indexes');
  const [documentImportOpen, setDocumentImportOpen] = useState(false);
  const [bulkPreviewOpen, setBulkPreviewOpen] = useState(false);
  const [fixtureModeVisible, setFixtureModeVisible] = useState(false);
  const [documentPickerSearch, setDocumentPickerSearch] = useState('');
  const [bulkPreview, setBulkPreview] = useState<BulkPreview | null>(null);
  const [importForm, setImportForm] = useState({
    mode: DEFAULT_OPERATION_MODE as 'fixture' | 'live',
    cycle: '',
    source: 'all',
  });
  const [documentImportForm, setDocumentImportForm] = useState({
    mode: DEFAULT_OPERATION_MODE as 'fixture' | 'live',
    cycle: '',
    initiative_number: '',
    document_types: [
      'sos_result_page', 'sos_final_text', 'legislative_detail_page', 'legislative_fiscal',
      'legislative_ballot_analysis_page', 'legislative_ballot_analysis_draft',
    ],
  });
  const [form, setForm] = useState(EMPTY_FORM);

  const cycles = useQuery({
    queryKey: ['admin-elections', 'cycles'],
    queryFn: async () => {
      const response = await api.get('/election-calendars');
      return (response.data.data ?? []) as ElectionCycle[];
    },
  });
  const cyclesNewestFirst = useMemo(
    () => [...(cycles.data ?? [])].sort((a, b) => b.election_year - a.election_year),
    [cycles.data]
  );

  const importOptions = useQuery({
    queryKey: ['ballot-initiative-import-options'],
    queryFn: async () => {
      const response = await api.get('/admin/ballot-initiatives/import-options');
      return response.data.data as BallotInitiativeImportOptions;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const options = importOptions.data;
    if (!options) return;
    setImportForm(current => ({
      ...current,
      cycle: resolveImportCycle(current.cycle, options),
    }));
    setDocumentImportForm(current => ({
      ...current,
      cycle: resolveImportCycle(current.cycle, options),
    }));
  }, [importOptions.data]);

  const districts = useQuery({
    queryKey: ['admin-elections', 'electoral-districts', 'CO'],
    queryFn: async () => {
      const response = await api.get('/reference/electoral-districts', { params: { state_code: 'CO' } });
      return (response.data.data?.districts ?? []) as ElectoralDistrictOption[];
    },
    staleTime: 5 * 60 * 1000,
    enabled: addOpen,
  });

  const list = useAdminList<InitiativeRow>({
    queryKey: 'elections-initiatives',
    endpoint: '/admin/ballot-initiatives',
    parse: parseInitiatives,
    filters: {
      measure_type: measureType || undefined,
      initiative_source: source || undefined,
      status: status || undefined,
      status_group: statusGroup || undefined,
      publication_status: publication && publication !== NEEDS_PUBLISH_FILTER ? publication : undefined,
      needs_publish: publication === NEEDS_PUBLISH_FILTER ? 'true' : undefined,
      blue_book_entry_status: blueBookStatus || undefined,
      jurisdiction_level: jurisdiction || undefined,
      election_calendar_id: cycleId || undefined,
      active: active || undefined,
    },
    sortParamNames: { sort: 'sortBy', order: 'sortDirection' },
  });

  const activeScopes = scopesForJurisdiction(form.jurisdiction_level);
  const scopedDistricts = activeScopes
    ? (districts.data ?? []).filter((district) => activeScopes.includes(district.scope))
    : [];
  const showDistrictSelect = activeScopes !== null && scopedDistricts.length > 0;
  const showJurisdictionName = form.jurisdiction_level !== 'statewide' && !showDistrictSelect;

  const openAdd = () => {
    // Preselect the newest configured cycle — the cycle is the container.
    const newest = cyclesNewestFirst[0];
    setForm({ ...EMPTY_FORM, election_calendar_id: newest?.id ?? '' });
    setAddOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      return api.post('/admin/ballot-initiatives', {
        ballot_title: form.ballot_title.trim() || null,
        measure_number: form.measure_number.trim() || undefined,
        popular_name: form.popular_name.trim() || undefined,
        description: normalizeRichText(form.description) || undefined,
        measure_type: form.measure_type,
        initiative_source: form.initiative_source,
        election_calendar_id: form.election_calendar_id,
        jurisdiction_level: form.jurisdiction_level,
        jurisdiction_name: form.jurisdiction_name.trim() || undefined,
        electoral_district_id: form.electoral_district_id || undefined,
        status: form.status,
      });
    },
    onSuccess: (response) => {
      toast.success('Ballot initiative created');
      setAddOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin-list', 'elections-initiatives'] });
      const created = response.data.data as InitiativeRow | undefined;
      if (created?.id) navigate(`/admin-next/elections/initiatives/${created.id}`);
    },
    onError: (error: unknown) => toast.error(apiError(error, 'Failed to create ballot initiative')),
  });

  const saveDisabled =
    form.measure_type === '' ||
    form.initiative_source === '' ||
    form.election_calendar_id === '';

  const importRuns = useQuery({
    queryKey: ['ballot-initiative-import-runs'],
    queryFn: async () => {
      const response = await api.get('/admin/ballot-initiatives/import-runs', { params: { limit: 5 } });
      return (response.data.data ?? []) as ImportRun[];
    },
    refetchInterval: (runs) => runs?.some(run => run.status === 'running') ? 3000 : false,
  });

  const importMutation = useMutation({
    mutationFn: () => api.post('/admin/ballot-initiatives/import', importForm),
    onSuccess: () => {
      toast.success(`${importForm.mode === 'fixture' ? 'Fixture' : 'Live'} import queued`);
      setImportOpen(false);
      queryClient.invalidateQueries({ queryKey: ['ballot-initiative-import-runs'] });
    },
    onError: (error: unknown) => toast.error(apiError(error, 'Failed to queue ballot initiative import')),
  });

  const documentImportMutation = useMutation({
    mutationFn: () => api.post(
      '/admin/ballot-initiatives/import-documents',
      documentImportForm
    ),
    onSuccess: () => {
      toast.success(
        `${documentImportForm.mode === 'fixture' ? 'Fixture' : 'Live'} document acquisition queued`
      );
      setDocumentImportOpen(false);
      queryClient.invalidateQueries({ queryKey: ['ballot-initiative-import-runs'] });
    },
    onError: (error: unknown) => toast.error(
      apiError(error, 'Failed to queue ballot initiative document acquisition')
    ),
  });

  const periodicRefreshMutation = useMutation({
    mutationFn: () => api.post('/admin/ballot-initiatives/periodic-refresh', { mode: importForm.mode }),
    onSuccess: () => {
      toast.success(`${importForm.mode === 'fixture' ? 'Fixture' : 'Live'} periodic refresh queued`);
      setImportOpen(false);
      queryClient.invalidateQueries({ queryKey: ['ballot-initiative-run-summaries'] });
      queryClient.invalidateQueries({ queryKey: ['ballot-initiative-operations-summary'] });
    },
    onError: (error: unknown) => toast.error(apiError(error, 'Failed to queue periodic refresh')),
  });

  const coveragePicker = useQuery({
    queryKey: documentCoverageQueryKey(
      documentImportForm.mode, documentImportForm.cycle, documentPickerSearch
    ),
    queryFn: () => fetchDocumentCoverage(
      documentImportForm.cycle, documentPickerSearch, undefined, documentImportForm.mode
    ),
    enabled: documentImportOpen && Boolean(documentImportForm.cycle),
    staleTime: 30_000,
  });

  const bulkPreviewMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/admin/ballot-initiatives/import-documents/bulk/preview', {
        mode: documentImportForm.mode,
        cycle: documentImportForm.cycle,
        document_types: documentImportForm.document_types,
      });
      return normalizeBulkPreview(response.data.data);
    },
    onSuccess: data => setBulkPreview(data),
    onError: (error: unknown) => toast.error(apiError(error, 'Failed to preview eligible documents')),
  });

  const bulkImportMutation = useMutation({
    mutationFn: () => api.post('/admin/ballot-initiatives/import-documents/bulk', {
      mode: documentImportForm.mode,
      cycle: documentImportForm.cycle,
      document_types: documentImportForm.document_types,
    }),
    onSuccess: () => {
      toast.success('Bounded document acquisition queued');
      setBulkPreviewOpen(false);
      queryClient.invalidateQueries({ queryKey: ['ballot-initiative-run-summaries'] });
      queryClient.invalidateQueries({ queryKey: ['ballot-initiative-operations-summary'] });
    },
    onError: (error: unknown) => toast.error(apiError(error, 'Failed to queue bulk document acquisition')),
  });

  const openDocumentImport = (entry?: DocumentCoverageEntry) => {
    setFixtureModeVisible(false);
    setDocumentPickerSearch(entry ? entry.initiativeNumber : '');
    setDocumentImportForm(current => ({
      ...current,
      mode: 'live',
      cycle: entry?.cycle || current.cycle || importOptions.data?.current_cycle || '',
      initiative_number: entry?.initiativeNumber || '',
    }));
    setDocumentImportOpen(true);
  };

  const selectedCoverage = coveragePicker.data?.entries.find(
    entry => entry.initiativeNumber === documentImportForm.initiative_number
  );

  const toggleDocumentType = (type: string, checked: boolean) => {
    setDocumentImportForm(current => ({
      ...current,
      document_types: toggleDocumentTypeSelection(current.document_types, type, checked),
    }));
  };

  const clearAllFilters = () => {
    setMeasureType(''); setSource(''); setStatus(''); setStatusGroup(''); setPublication('');
    setBlueBookStatus(''); setJurisdiction(''); setCycleId(''); setActive('');
    list.setSearch('');
  };

  const latestRun = importRuns.data?.[0];
  const failedSnapshots = latestRun?.snapshots.filter(snapshot => snapshot.parse_state === 'failed') ?? [];
  const operationActions = (
    <div className="relative flex items-center gap-2">
      <Button variant="outline" size="sm" icon={UploadIcon} onClick={() => { setImportAction('indexes'); setFixtureModeVisible(false); setImportForm(current => ({ ...current, mode: 'live' })); setImportOpen(true); }}>Refresh Source Indexes</Button>
      <Button variant="outline" size="sm" icon={MoreHorizontal} onClick={() => setActionsOpen(open => !open)} aria-expanded={actionsOpen}>More actions</Button>
      {actionsOpen && <div className="absolute right-0 top-full z-20 mt-1 w-64 rounded-lg border border-border-light bg-white p-1 shadow-lg dark:border-border-dark dark:bg-background-dark-secondary"><button type="button" className="flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary" onClick={() => { setActionsOpen(false); openDocumentImport(); }}>Acquire one initiative's documents</button><button type="button" className="flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary" onClick={() => { setActionsOpen(false); setFixtureModeVisible(false); setDocumentImportForm(current => ({ ...current, mode: 'live' })); setBulkPreview(null); setBulkPreviewOpen(true); }}>Acquire eligible missing/revised PDFs</button><button type="button" className="flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary" onClick={() => { setActionsOpen(false); setImportAction('indexes'); setImportForm(current => ({ ...current, mode: 'fixture' })); setFixtureModeVisible(true); setImportOpen(true); }}>Run fixture validation</button><button type="button" className="flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary" onClick={() => { setActionsOpen(false); setExplainerImportOpen(true); }}>Import explainers (folder)</button><button type="button" className="flex min-h-11 w-full items-center rounded-md px-3 text-left text-sm hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary" onClick={() => { setActionsOpen(false); openAdd(); }}>Add initiative manually</button></div>}
    </div>
  );

  return (
    <>
    <BallotInitiativeOperations
      activeView={operationsView}
      cycle={importOptions.data?.current_cycle ?? ''}
      onViewChange={setOperationsView}
      onRefresh={() => {
        setImportForm(current => ({ ...current, mode: 'live', cycle: importOptions.data?.current_cycle ?? current.cycle, source: 'all' }));
        setFixtureModeVisible(false);
        setImportAction('periodic');
        setImportOpen(true);
      }}
      onAcquireOne={openDocumentImport}
      onBulkPreview={() => { setFixtureModeVisible(false); setDocumentImportForm(current => ({ ...current, mode: 'live' })); setBulkPreview(null); setBulkPreviewOpen(true); }}
      onNeedsPublish={() => {
        // The tile's count is "on the ballot by status AND unpublished"; any
        // stage/status/lifecycle filter left applied would AND against it and
        // show fewer rows than the tile promised. Clear those, keep the cycle.
        setOperationsView('initiatives');
        setStatus(''); setStatusGroup(''); setActive(''); setBlueBookStatus('');
        list.setSearch('');
        setPublication(NEEDS_PUBLISH_FILTER);
      }}
      actions={operationActions}
    >
    <div className="space-y-3">
      <AdminListToolbar
        search={list.search}
        onSearch={list.setSearch}
        searchPlaceholder="Search by title, name, or number…"
        searchAriaLabel="Search ballot initiatives"
      >
        <select
          value={cycleId}
          onChange={(event) => setCycleId(event.target.value)}
          aria-label="Filter by election cycle"
          className={filterSelectClasses}
        >
          <option value="">All cycles</option>
          {cyclesNewestFirst.map((cycle) => (
            <option key={cycle.id} value={cycle.id}>
              {cycle.name || `${cycle.election_year} ${cycle.state} ${cycle.election_type}`}
            </option>
          ))}
        </select>
        <select
          value={statusGroup}
          onChange={(event) => setStatusGroup(event.target.value)}
          aria-label="Filter by pipeline stage"
          className={filterSelectClasses}
        >
          <option value="">Any stage</option>
          {STATUS_GROUPS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label="Filter by status"
          className={filterSelectClasses}
        >
          <option value="">All statuses</option>
          {INITIATIVE_STATUSES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={publication}
          onChange={(event) => setPublication(event.target.value)}
          aria-label="Filter by publication"
          className={filterSelectClasses}
        >
          <option value="">Any publication</option>
          {PUBLICATION_STATUSES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          <option value={NEEDS_PUBLISH_FILTER}>Needs publish</option>
        </select>
        <HelpTrigger contentKey="initiative-publication" size="sm" ariaLabel="Help for publication" />
        <Button variant="outline" size="sm" icon={ChevronDown} onClick={() => setMoreFiltersOpen(open => !open)} aria-expanded={moreFiltersOpen}>More filters</Button>
        {(list.search || measureType || source || status || statusGroup || publication || blueBookStatus || jurisdiction || cycleId || active !== '') && <Button variant="ghost" size="sm" onClick={clearAllFilters}>Clear all</Button>}
      </AdminListToolbar>

      {(measureType || source || status || statusGroup || publication || blueBookStatus || jurisdiction || cycleId || active !== '') && (
        <div className="flex flex-wrap items-center gap-2" aria-label="Applied filters">
          {cycleId && <Button variant="ghost" size="sm" onClick={() => setCycleId('')}>Cycle selected ×</Button>}
          {statusGroup && <Button variant="ghost" size="sm" onClick={() => setStatusGroup('')}>Stage: {statusGroup.replace(/_/g, ' ')} ×</Button>}
          {status && <Button variant="ghost" size="sm" onClick={() => setStatus('')}>Status: {statusLabel(status)} ×</Button>}
          {publication && <Button variant="ghost" size="sm" onClick={() => setPublication('')}>Publication: {publication === NEEDS_PUBLISH_FILTER ? 'needs publish' : publicationLabel(publication)} ×</Button>}
          {measureType && <Button variant="ghost" size="sm" onClick={() => setMeasureType('')}>Type: {measureTypeLabel(measureType)} ×</Button>}
          {source && <Button variant="ghost" size="sm" onClick={() => setSource('')}>Source: {initiativeSourceLabel(source)} ×</Button>}
          {jurisdiction && <Button variant="ghost" size="sm" onClick={() => setJurisdiction('')}>Jurisdiction: {jurisdictionLabel(jurisdiction)} ×</Button>}
          {blueBookStatus && <Button variant="ghost" size="sm" onClick={() => setBlueBookStatus('')}>Blue Book: {blueBookStatus.replace(/_/g, ' ')} ×</Button>}
          {active !== '' && <Button variant="ghost" size="sm" onClick={() => setActive('')}>Lifecycle: {active === 'false' ? 'ended or missing' : 'current'} ×</Button>}
        </div>
      )}

      {moreFiltersOpen && (
        <div className="card flex flex-wrap items-center gap-2 p-3" aria-label="Additional ballot initiative filters">
          <select value={measureType} onChange={(event) => setMeasureType(event.target.value)} aria-label="Filter by measure type" className={filterSelectClasses}><option value="">All types</option>{MEASURE_TYPES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
          <select value={source} onChange={(event) => setSource(event.target.value)} aria-label="Filter by source" className={filterSelectClasses}><option value="">All sources</option>{INITIATIVE_SOURCES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
          <select value={active} onChange={(event) => setActive(event.target.value)} aria-label="Filter by official-source lifecycle" className={filterSelectClasses}><option value="true">Official-source lifecycle: current</option><option value="false">Official-source lifecycle: ended or missing</option><option value="">Official-source lifecycle: all</option></select>
          <select value={blueBookStatus} onChange={(event) => setBlueBookStatus(event.target.value)} aria-label="Filter by Blue Book entry status" className={filterSelectClasses}><option value="">Any Blue Book state</option><option value="pending">Blue Book: pending</option><option value="entered">Blue Book: entered</option><option value="verified">Blue Book: verified</option><option value="not_applicable">Blue Book: n/a</option></select>
          <select value={jurisdiction} onChange={(event) => setJurisdiction(event.target.value)} aria-label="Filter by jurisdiction" className={filterSelectClasses}><option value="">All jurisdictions</option>{JURISDICTION_LEVELS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
        </div>
      )}

      {list.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <WarningIcon className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load ballot initiatives. Please try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
        <div className="space-y-3 2xl:hidden">
          {list.isLoading ? (
            <div className="card p-8 text-center text-sm text-text-secondary dark:text-text-dark-secondary">Loading initiatives…</div>
          ) : list.rows.length === 0 ? (
            <div className="card p-8 text-center text-sm text-text-secondary dark:text-text-dark-secondary">No ballot initiatives found.</div>
          ) : list.rows.map(row => (
            <article key={row.id} className="card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <Link to={`/admin-next/elections/initiatives/${row.id}`} className="min-h-11 min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                  <span className="block text-xs text-text-tertiary dark:text-text-dark-tertiary">{row.election_calendar_name || row.election_calendar?.name || row.election_calendar?.election_year || 'Cycle unavailable'} · {initiativeNumberLine(row) || 'Number unavailable'}</span>
                  <span className="mt-1 block font-medium text-primary-700 dark:text-primary-300">{initiativeDisplayName(row)}</span>
                </Link>
                <span className="flex flex-wrap items-center gap-1.5">
                  <Chip label={statusLabel(row.status)} variant={statusVariant(row.status)} size="sm" />
                  <Chip label={publicationLabel(row.publication_status)} tone={publicationTone(row.publication_status)} size="sm" />
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2"><Chip label={measureTypeLabel(row.measure_type)} variant="neutral" size="xs" /><span className="text-xs text-text-secondary dark:text-text-dark-secondary">{initiativeSourceLabel(row.initiative_source)}</span></div>
                <Button variant="outline" size="sm" onClick={() => openDocumentImport({ initiativeId: row.id, cycle: sourceCycleForRow(row, importOptions.data?.current_cycle || ''), initiativeNumber: row.sos_initiative_number || '', measureNumber: row.measure_number, popularName: row.popular_name, unofficialCaption: row.unofficial_caption, ballotTitle: row.ballot_title, status: row.status, documents: {} })} disabled={!row.sos_initiative_number}>Acquire documents</Button>
              </div>
            </article>
          ))}
        </div>
        <div className="hidden 2xl:block">
        <StandardTable horizontalScroll loading={list.isFetching && !list.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell
                sortable sortKey="measure_number" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort} columnType="fit"
              >
                Number
              </StandardTableHeaderCell>
              <StandardTableHeaderCell
                sortable sortKey="ballot_title" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort}
              >
                Title
              </StandardTableHeaderCell>
              <StandardTableHeaderCell
                sortable sortKey="measure_type" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort} columnType="fit"
              >
                Type
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit" responsiveHide="md">
                Source
              </StandardTableHeaderCell>
              <StandardTableHeaderCell
                sortable sortKey="jurisdiction_level" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort}
                columnType="fit" responsiveHide="lg"
              >
                Jurisdiction
              </StandardTableHeaderCell>
              <StandardTableHeaderCell
                sortable sortKey="election_cycle" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort} columnType="fit"
              >
                Cycle
              </StandardTableHeaderCell>
              <StandardTableHeaderCell
                sortable sortKey="status" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort} columnType="fit"
              >
                Status
              </StandardTableHeaderCell>
              <StandardTableHeaderCell
                sortable sortKey="publication_status" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort} columnType="fit"
              >
                Publication
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Actions</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {list.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : list.rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message={
                  list.search
                    ? `No ballot initiatives match "${list.search}"`
                    : 'No ballot initiatives found'
                }
                icon={<Vote className="h-8 w-8" />}
              />
            ) : (
              list.rows.map((row) => (
                <StandardTableRow
                  key={row.id}
                >
                  <StandardTableCell>
                    <Link
                      to={`/admin-next/elections/initiatives/${row.id}`}
                      className="inline-flex min-h-11 items-center font-medium text-primary-700 hover:underline dark:text-primary-300"
                    >
                      {initiativeNumberLine(row) || '—'}
                    </Link>
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="flex flex-col">
                      <Link
                        to={`/admin-next/elections/initiatives/${row.id}`}
                        className="line-clamp-1 font-medium text-primary-700 hover:underline dark:text-primary-300"
                      >
                        {initiativeDisplayName(row)}
                      </Link>
                      {row.popular_name && row.ballot_title
                        && row.popular_name !== row.ballot_title && (
                        <span className="line-clamp-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          {row.ballot_title}
                        </span>
                      )}
                    </div>
                  </StandardTableCell>
                  <StandardTableCell>
                    <Chip label={measureTypeLabel(row.measure_type)} variant="neutral" size="sm" />
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">
                    {initiativeSourceLabel(row.initiative_source)}
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">
                    {row.jurisdiction_name
                      ? `${jurisdictionLabel(row.jurisdiction_level)}: ${row.jurisdiction_name}`
                      : jurisdictionLabel(row.jurisdiction_level)}
                  </StandardTableCell>
                  <StandardTableCell>
                    {row.election_calendar_name
                      || (row.election_calendar
                        ? `${row.election_calendar.election_year} ${row.election_calendar.election_type ?? ''}`.trim()
                        : '—')}
                  </StandardTableCell>
                  <StandardTableCell>
                    <Chip label={statusLabel(row.status)} variant={statusVariant(row.status)} size="sm" />
                  </StandardTableCell>
                  <StandardTableCell>
                    <Chip label={publicationLabel(row.publication_status)} tone={publicationTone(row.publication_status)} size="sm" />
                  </StandardTableCell>
                  <StandardTableCell>
                    <Button variant="ghost" size="sm" onClick={(event: React.MouseEvent<HTMLButtonElement>) => { event.stopPropagation(); openDocumentImport({ initiativeId: row.id, cycle: sourceCycleForRow(row, importOptions.data?.current_cycle || ''), initiativeNumber: row.sos_initiative_number || '', measureNumber: row.measure_number, popularName: row.popular_name, unofficialCaption: row.unofficial_caption, ballotTitle: row.ballot_title, status: row.status, documents: {} }); }} disabled={!row.sos_initiative_number}>Documents</Button>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
        </div>
        </>
      )}

      {latestRun && (
        <section className="card space-y-2 p-4" aria-label="Latest ballot initiative import">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-medium text-text-primary dark:text-text-dark-primary">
                {latestRun.mode === 'fixture' ? 'Latest test-evidence import' : 'Latest live official-source import'}
              </h3>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {latestRun.operation === 'document_acquisition'
                  ? `documents for #${latestRun.initiative_number}`
                  : (latestRun.operation === 'periodic_refresh' ? 'periodic refresh' : 'index import')}
                {' · '}{latestRun.mode === 'fixture' ? 'Test evidence' : 'Live official sources'} · {latestRun.initiative_cycle} · {latestRun.status}
              </p>
              {latestRun.operation === 'periodic_refresh' && (
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {latestRun.summary.no_material_changes === true
                    ? 'No material changes detected'
                    : `${Number(latestRun.summary.material_changes || 0)} material change${Number(latestRun.summary.material_changes || 0) === 1 ? '' : 's'}`}
                  {' · '}{Number(latestRun.summary.unchanged_observations || 0)} unchanged observation{Number(latestRun.summary.unchanged_observations || 0) === 1 ? '' : 's'}
                </p>
              )}
            </div>
            <Chip
              label={failedSnapshots.length > 0 ? `${failedSnapshots.length} retained failure${failedSnapshots.length === 1 ? '' : 's'}` : 'No source failures'}
              variant={failedSnapshots.length > 0 ? 'warning' : 'success'}
              size="sm"
            />
          </div>
          {failedSnapshots.length > 0 && (
            <ul className="space-y-1 text-sm text-text-secondary dark:text-text-dark-secondary">
              {failedSnapshots.map(snapshot => (
                <li key={snapshot.id} className="flex items-start gap-2">
                  <WarningIcon className="mt-0.5 h-4 w-4 shrink-0 text-warning-600" />
                  <span>
                    {snapshot.source_code}: {snapshot.http_status ? `HTTP ${snapshot.http_status}` : 'transport failure'}
                    {snapshot.failure_message ? ` — ${snapshot.failure_message}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {latestRun.documents?.length > 0 && (
            <ul className="space-y-1 text-sm text-text-secondary dark:text-text-dark-secondary">
              {latestRun.documents.map(document => (
                <li key={document.id} className="flex flex-wrap items-center gap-2">
                  <Chip
                    label={document.extraction_state}
                    variant={document.extraction_state === 'extracted' ? 'success' : 'warning'}
                    size="sm"
                  />
                  <span>{documentTypeLabel(document.document_type)}: {document.exact_label}</span>
                  {document.failure_message && <span>— {document.failure_message}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {!list.isError && (
        <ListPager
          offset={list.offset}
          limit={list.limit}
          total={list.total}
          page={list.page}
          pageCount={list.pageCount}
          hasPrev={list.hasPrev}
          hasNext={list.hasNext}
          onPrev={list.prevPage}
          onNext={list.nextPage}
          noun="initiatives"
        />
      )}
    </div>
    </BallotInitiativeOperations>
      <ExplainerBulkImportModal
        cycle={importOptions.data?.current_cycle ?? ''}
        isOpen={explainerImportOpen}
        onClose={() => setExplainerImportOpen(false)}
        onImported={() => {
          queryClient.invalidateQueries({ queryKey: ['admin-list', 'elections-initiatives'] });
          queryClient.invalidateQueries({ queryKey: ['ballot-initiative-operations-summary'] });
        }}
      />

      <AdminModal
        isOpen={importOpen}
        title={importAction === 'periodic' ? 'Refresh Current Official Data' : 'Refresh Source Indexes'}
        subtitle={importAction === 'periodic'
          ? 'Checks both current-cycle indexes and refreshes eligible linked HTML'
          : 'Acquires the selected official index and persists parsed source observations'}
        onClose={() => setImportOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" className="min-h-11" onClick={() => setImportOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="min-h-11"
              loading={importMutation.isLoading || periodicRefreshMutation.isLoading}
              disabled={!importForm.cycle || importOptions.isError}
              onClick={() => importAction === 'periodic' ? periodicRefreshMutation.mutate() : importMutation.mutate()}
            >
              {importAction === 'periodic' ? 'Start Refresh' : 'Refresh Indexes'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-lg border border-border-light p-3 dark:border-border-dark">
            <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Live official acquisition</p>
            <p className="mt-1 text-xs text-text-secondary dark:text-text-dark-secondary">Uses bounded, fixed Colorado source URLs and retains every response before parsing.</p>
            <button type="button" className="mt-3 min-h-11 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400" aria-expanded={fixtureModeVisible} onClick={() => setFixtureModeVisible(visible => !visible)}>Advanced test mode</button>
            {fixtureModeVisible && <label className="mt-2 flex items-start gap-2 rounded-lg bg-warning-50 p-3 text-sm dark:bg-warning-900/20"><input type="checkbox" checked={importForm.mode === 'fixture'} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setImportForm(current => ({ ...current, mode: event.target.checked ? 'fixture' : 'live' }))} /><span><strong>Use fixture evidence.</strong> This creates deterministic admin-only test history and does not contact official sources.</span></label>}
          </div>
          {importAction === 'indexes' && <>
          <div>
            <label htmlFor="initiative-import-cycle" className={labelClasses}>Initiative cycle</label>
            <select
              id="initiative-import-cycle"
              value={importForm.cycle}
              onChange={(event) => setImportForm(current => ({ ...current, cycle: event.target.value }))}
              className={inputClasses}
            >
              {!importOptions.data && <option value="">Loading configured cycles…</option>}
              {(importOptions.data?.cycles ?? []).map(cycle => (
                <option key={cycle} value={cycle}>{cycle}</option>
              ))}
            </select>
            {importOptions.isError && (
              <p className="mt-1 text-xs text-red-600">Configured cycles could not be loaded.</p>
            )}
          </div>
          <div>
            <label htmlFor="initiative-import-source" className={labelClasses}>Source scope</label>
            <select
              id="initiative-import-source"
              value={importForm.source}
              onChange={(event) => setImportForm(current => ({ ...current, source: event.target.value }))}
              className={inputClasses}
            >
              <option value="all">SOS and Legislative Council</option>
              <option value="sos">Secretary of State only</option>
              <option value="legislative_council">Legislative Council only</option>
            </select>
          </div>
          </>}
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            {importAction === 'periodic'
              ? 'This acquires both indexes, refreshes new or changed result/detail HTML, reuses existing PDFs, and records material changes and failures.'
              : 'This step does not bulk-acquire PDFs. Use Document Coverage afterward to preview missing or revised documents.'}
          </p>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={documentImportOpen}
        title="Acquire Official Source Documents"
        subtitle="Refreshes linked source HTML and acquires only missing or revised official PDFs"
        onClose={() => setDocumentImportOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" className="min-h-11" onClick={() => setDocumentImportOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="min-h-11"
              loading={documentImportMutation.isLoading}
              disabled={
                !/^[1-9]\d*$/.test(documentImportForm.initiative_number)
                || documentImportForm.document_types.length === 0
                || !documentImportForm.cycle
                || importOptions.isError
              }
              onClick={() => documentImportMutation.mutate()}
            >
              Acquire Documents
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="rounded-lg border border-border-light p-3 dark:border-border-dark">
            <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Live official acquisition</p>
            <p className="mt-1 text-xs text-text-secondary dark:text-text-dark-secondary">Existing successful PDFs at the same official URL are reused. Routine acquisition does not verify silent same-URL replacements.</p>
            <button type="button" className="mt-3 min-h-11 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400" aria-expanded={fixtureModeVisible} onClick={() => setFixtureModeVisible(visible => !visible)}>Advanced test mode</button>
            {fixtureModeVisible && <label className="mt-2 flex items-start gap-2 rounded-lg bg-warning-50 p-3 text-sm dark:bg-warning-900/20"><input type="checkbox" checked={documentImportForm.mode === 'fixture'} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDocumentImportForm(current => ({ ...current, mode: event.target.checked ? 'fixture' : 'live' }))} /><span><strong>Use fixture evidence.</strong> Fixture results remain admin-only and cannot replace live-owned projections.</span></label>}
          </div>
          <div>
            <label htmlFor="initiative-document-cycle" className={labelClasses}>Initiative cycle</label>
            <select
              id="initiative-document-cycle"
              value={documentImportForm.cycle}
              onChange={(event) => setDocumentImportForm(current => ({
                ...current, cycle: event.target.value,
              }))}
              className={inputClasses}
            >
              {!importOptions.data && <option value="">Loading configured cycles…</option>}
              {(importOptions.data?.cycles ?? []).map(cycle => (
                <option key={cycle} value={cycle}>{cycle}</option>
              ))}
            </select>
            {importOptions.isError && (
              <p className="mt-1 text-xs text-red-600">Configured cycles could not be loaded.</p>
            )}
          </div>
          <div>
            <label htmlFor="initiative-document-number" className={labelClasses}>Initiative</label>
            <input type="search" value={documentPickerSearch} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDocumentPickerSearch(event.target.value)} placeholder="Search by caption or initiative number" className={`${inputClasses} mb-2`} aria-label="Search retained initiatives" />
            <select id="initiative-document-number" value={documentImportForm.initiative_number} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setDocumentImportForm(current => ({ ...current, initiative_number: event.target.value }))} className={inputClasses}>
              <option value="">Select a retained initiative…</option>
              {coveragePicker.data?.entries.map(entry => <option key={entry.initiativeId} value={entry.initiativeNumber}>{entry.cycle} · Initiative #{entry.initiativeNumber} · {entry.popularName || entry.unofficialCaption || entry.ballotTitle || 'Untitled'}</option>)}
            </select>
            {coveragePicker.isLoading && <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">Loading retained initiatives…</p>}
            {coveragePicker.isError && <p className="mt-1 text-xs text-error-600 dark:text-error-300">Initiative coverage could not be loaded.</p>}
            {coveragePicker.data?.hasMore && <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">More matches exist. Refine the search to reach the intended initiative.</p>}
          </div>
          <fieldset className="space-y-3">
            <legend className={labelClasses}>Official documents</legend>
            <div className="rounded-lg border border-border-light p-3 dark:border-border-dark"><p className="mb-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary">Secretary of State</p>{[['sos_result_page', 'Refresh result page'], ['sos_final_text', 'Acquire missing or revised final-text PDF']].map(([value, label]) => <label key={value} className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={documentImportForm.document_types.includes(value)} onChange={(event: React.ChangeEvent<HTMLInputElement>) => toggleDocumentType(value, event.target.checked)} /><span>{label}{value === 'sos_final_text' && ' (includes result page)'}</span></label>)}</div>
            <div className="rounded-lg border border-border-light p-3 dark:border-border-dark"><p className="mb-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary">Legislative Council</p>{[['legislative_detail_page', 'Refresh initiative detail page'], ['legislative_fiscal', 'Acquire missing or revised fiscal PDF'], ['legislative_ballot_analysis_page', 'Refresh ballot analysis detail page'], ['legislative_ballot_analysis_draft', 'Acquire ballot analysis draft PDF']].map(([value, label]) => <label key={value} className="flex min-h-11 items-center gap-2 text-sm"><input type="checkbox" checked={documentImportForm.document_types.includes(value)} onChange={(event: React.ChangeEvent<HTMLInputElement>) => toggleDocumentType(value, event.target.checked)} /><span>{label}{value === 'legislative_fiscal' && ' (includes initiative detail page)'}{value === 'legislative_ballot_analysis_draft' && ' (includes analysis detail page)'}</span></label>)}</div>
          </fieldset>
          {selectedCoverage && <div className="rounded-lg bg-background-secondary p-3 dark:bg-background-dark-tertiary"><p className="mb-2 text-sm font-medium text-text-primary dark:text-text-dark-primary">What this action will do</p><ul className="space-y-1 text-xs text-text-secondary dark:text-text-dark-secondary">{documentImportForm.document_types.map(type => <li key={type}><strong>{type.replace(/_/g, ' ')}:</strong> {documentActionPreview(type, selectedCoverage.documents[type], documentImportForm.document_types)}</li>)}</ul></div>}
          {!documentImportForm.initiative_number && <p className="text-sm text-warning-700 dark:text-warning-300" role="status">Select an initiative to enable acquisition.</p>}
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            URLs are resolved only from retained official-source observations. No arbitrary URL is accepted.
          </p>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={bulkPreviewOpen}
        title="Preview Eligible Bulk Acquisition"
        subtitle="Builds the same server-generated manifest used by execution without queuing work"
        onClose={() => setBulkPreviewOpen(false)}
        size="lg"
        footer={<><Button variant="secondary" size="sm" className="min-h-11" onClick={() => setBulkPreviewOpen(false)}>Close</Button><Button variant="outline" size="sm" className="min-h-11" loading={bulkPreviewMutation.isLoading} disabled={!documentImportForm.cycle} onClick={() => bulkPreviewMutation.mutate()}>Preview Manifest</Button>{bulkPreview && <Button variant="primary" size="sm" className="min-h-11" loading={bulkImportMutation.isLoading} onClick={() => bulkImportMutation.mutate()}>Start Bounded Acquisition</Button>}</>}
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-border-light p-3 dark:border-border-dark"><p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Live official acquisition</p><button type="button" className="mt-2 min-h-11 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400" aria-expanded={fixtureModeVisible} onClick={() => setFixtureModeVisible(visible => !visible)}>Advanced test mode</button>{fixtureModeVisible && <label className="mt-2 flex items-start gap-2 rounded-lg bg-warning-50 p-3 text-sm dark:bg-warning-900/20"><input type="checkbox" checked={documentImportForm.mode === 'fixture'} onChange={(event: React.ChangeEvent<HTMLInputElement>) => { setBulkPreview(null); setDocumentImportForm(current => ({ ...current, mode: event.target.checked ? 'fixture' : 'live' })); }} /><span><strong>Use fixture evidence.</strong> The preview and execution remain admin-only and make no official requests.</span></label>}</div>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Existing successful PDFs are reused. Each execution processes at most 250 server-selected identities and a later execution resumes from the durable checkpoint.</p>
          {!bulkPreview && !bulkPreviewMutation.isLoading && <div className="rounded-lg bg-background-secondary p-4 text-sm dark:bg-background-dark-tertiary">Previewing is read-only and makes no official-source requests.</div>}
          {bulkPreview && <dl className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-live="polite"><div><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Eligible identities</dt><dd className="text-xl font-semibold">{bulkPreview.eligibleIdentityCount}</dd></div><div><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Next execution</dt><dd className="text-xl font-semibold">{bulkPreview.identitiesInNextExecution}</dd></div><div><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">PDFs reused</dt><dd className="text-xl font-semibold">{bulkPreview.pdfsReused}</dd></div><div><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">PDFs to acquire</dt><dd className="text-xl font-semibold">{bulkPreview.pdfsToAcquire}</dd></div><div className="col-span-2 md:col-span-4"><dt className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Manifest identity</dt><dd className="break-all font-mono text-xs">{bulkPreview.manifestSha256}</dd></div></dl>}
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">Starting acquisition uses this fixed selection policy, retains older artifacts, and pauses at the durable execution boundary when more work remains.</p>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={addOpen}
        title="Add Ballot Initiative"
        subtitle="All data comes from the Secretary of State"
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={saveDisabled}
              loading={createMutation.isLoading}
              onClick={() => createMutation.mutate()}
            >
              Create Initiative
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="initiative-title" className={labelClasses}>Ballot Title</label>
            <textarea
              id="initiative-title"
              rows={2}
              value={form.ballot_title}
              placeholder="Official SOS ballot title, when available"
              onChange={(event) => setForm((current) => ({ ...current, ballot_title: event.target.value }))}
              className={inputClasses}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="initiative-number" className={labelClasses}>Measure Number</label>
              <input
                id="initiative-number"
                type="text"
                value={form.measure_number}
                placeholder="e.g. Amendment A"
                onChange={(event) => setForm((current) => ({ ...current, measure_number: event.target.value }))}
                className={inputClasses}
              />
            </div>
            <div>
              <label htmlFor="initiative-popular-name" className={labelClasses}>Popular Name</label>
              <input
                id="initiative-popular-name"
                type="text"
                value={form.popular_name}
                placeholder="Short public name"
                onChange={(event) => setForm((current) => ({ ...current, popular_name: event.target.value }))}
                className={inputClasses}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="initiative-type" className={labelClasses}>Measure Type *</label>
              <select
                id="initiative-type"
                value={form.measure_type}
                onChange={(event) => setForm((current) => ({ ...current, measure_type: event.target.value }))}
                className={inputClasses}
              >
                <option value="">Select type…</option>
                {MEASURE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="initiative-source" className={labelClasses}>Initiative Source *</label>
              <select
                id="initiative-source"
                value={form.initiative_source}
                onChange={(event) => setForm((current) => ({ ...current, initiative_source: event.target.value }))}
                className={inputClasses}
              >
                <option value="">Select source…</option>
                {INITIATIVE_SOURCES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="initiative-cycle" className={labelClasses}>Election Cycle *</label>
              <select
                id="initiative-cycle"
                value={form.election_calendar_id}
                onChange={(event) => setForm((current) => ({ ...current, election_calendar_id: event.target.value }))}
                className={inputClasses}
              >
                <option value="">Select cycle…</option>
                {(cycles.data ?? []).map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name || `${cycle.election_year} ${cycle.state} ${cycle.election_type}`}
                  </option>
                ))}
              </select>
              {(cycles.data ?? []).length === 0 && !cycles.isLoading && (
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  No cycles configured — add one in the Election Cycles tab first.
                </p>
              )}
            </div>
            <div>
              <label htmlFor="initiative-status" className={labelClasses}>Status</label>
              <select
                id="initiative-status"
                value={form.status}
                onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
                className={inputClasses}
              >
                {INITIATIVE_STATUSES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="initiative-jurisdiction" className={labelClasses}>Jurisdiction Level</label>
              <select
                id="initiative-jurisdiction"
                value={form.jurisdiction_level}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    jurisdiction_level: event.target.value,
                    jurisdiction_name: '',
                    electoral_district_id: '',
                  }))
                }
                className={inputClasses}
              >
                {JURISDICTION_LEVELS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            {showDistrictSelect && (
              <div>
                <label htmlFor="initiative-district" className={labelClasses}>
                  {form.jurisdiction_level === 'county' ? 'County' : 'District'}
                </label>
                <select
                  id="initiative-district"
                  value={form.electoral_district_id}
                  onChange={(event) => {
                    const district = scopedDistricts.find((entry) => entry.id === event.target.value);
                    setForm((current) => ({
                      ...current,
                      electoral_district_id: event.target.value,
                      jurisdiction_name: district?.display_label ?? '',
                    }));
                  }}
                  className={inputClasses}
                >
                  <option value="">
                    Select {form.jurisdiction_level === 'county' ? 'county' : 'district'}…
                  </option>
                  {scopedDistricts.map((district) => (
                    <option key={district.id} value={district.id}>{district.display_label}</option>
                  ))}
                </select>
              </div>
            )}
            {showJurisdictionName && (
              <div>
                <label htmlFor="initiative-jurisdiction-name" className={labelClasses}>Jurisdiction Name</label>
                <input
                  id="initiative-jurisdiction-name"
                  type="text"
                  value={form.jurisdiction_name}
                  placeholder="e.g. City of Boulder"
                  onChange={(event) => setForm((current) => ({ ...current, jurisdiction_name: event.target.value }))}
                  className={inputClasses}
                />
              </div>
            )}
          </div>
          <div>
            <span className={labelClasses}>SOS Full Title</span>
            <QuillEditor
              value={form.description}
              placeholder="Enter the title as designated and fixed by the Board"
              onChange={(value) => setForm((current) => ({ ...current, description: value }))}
            />
          </div>
        </div>
      </AdminModal>
    </>
  );
};

export default InitiativesView;
